import axios from "axios";
import { processEncryptedResponse } from "../utils/ResponseDecreypt";
import useExpiredToken from "../utils/useExpiredToken";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "../utils/Toast";
import { useSelector } from "react-redux";
import { useLoader } from "../contexts/LoaderContext";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
const showDecryptedLogs = process.env.REACT_APP_LOG_DECRYPTED_DATA === "true";

export const getApi = async (endpoint, token, expireToken) => {
  try {
    const res = await axios.get(`${BASE_URL}/${endpoint}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("getApi response data:", decryptedData);
    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Login API error:", error.response);
    if (error.status === 401) {
      expireToken();
    }
    return false;
  }
};

export const actionPost = async (endpoint, token, action, data, expireToken) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/${endpoint}`,
      {
        action,
        input: data,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("actionPost response data:", decryptedData);

    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Login API error:::::::::::::::::", error.response?.data);
    if (error.status === 401) {
      expireToken();
    }
    return error.response.data;
  }
};

export const logOut = async (endpoint, token, data) => {
  try {
    const res = await axios.post(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res) {
      return res.data;
    }
  } catch (error) {
    console.error("Login API error:", error.response);
    return false;
  }
};

export const assignPost = async (endpoint, token, data, expireToken, toast = null) => {
  try {
    const res = await axios.post(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("assignPost response data:", decryptedData);
    toast && toast.success(decryptedData?.message || decryptedData);
    if (res.status) {
      return res;
    } else {
      return res;
    }
  } catch (error) {
    const decryptedData = await processEncryptedResponse(error.response.data);
    showDecryptedLogs && console.log("assignPost response data:", decryptedData);
    toast && toast.error(decryptedData?.message || decryptedData);
    if (error.status === 401) {
      expireToken();
    }
    return error.response;
  }
};

export const assignPatch = async (endpoint, token, data, expireToken, toast = null) => {
  try {
    const res = await axios.put(`${BASE_URL}/${endpoint}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const decryptedData = await processEncryptedResponse(res.data);
    showDecryptedLogs && console.log("assignPatch response data:", decryptedData);
    toast && toast.success(decryptedData?.message || decryptedData);
    if (res.status) {
      return res;
    } else {
      return res;
    }
  } catch (error) {
    const decryptedData = await processEncryptedResponse(error.response.data);
    showDecryptedLogs && console.log("assignPatch response data:", decryptedData);
    toast && toast.error(decryptedData?.message || decryptedData);
    if (error.status === 401) {
      expireToken();
    }
    return error.response;
  }
};

export const deleteApi = async (endpoint, token, expireToken) => {
  try {
    const res = await axios.delete(`${BASE_URL}/${endpoint}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status) {
      return res.data;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Login API error:", error.response);
    if (error.status === 401) {
      expireToken();
    }
    return false;
  }
};

export const useApiCall = () => {
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const expireToken = useExpiredToken();
  const token = useSelector((state) => state.auth.jwtToken);

  const callApi = useCallback(
    async ({
      endpoint = "",
      bodyData = {},
      queryParams = {},
      showToast = true,
      apiMethod = "get",
      useToken = true,
      loader = true,
    }) => {
      loader && showLoader();
      setLoading(true);
      try {
        const method = apiMethod.toLowerCase();
        // normalize URL parts to avoid double slashes
        const url = `${BASE_URL.replace(/\/$/, "")}/${endpoint.replace(/^\//, "")}`;

        const config = {
          headers: { ...(useToken && { Authorization: `Bearer ${token}` }) },
          params: queryParams, // axios builds the ?a=1&b=2 string for you
          // optional: paramsSerializer if you need array handling
        };

        let res, status, message;
        if (["get", "delete"].includes(method)) {
          res = await axios[method](url, config);
        } else {
          res = await axios[method](url, bodyData, config);
        }

        const decryptedData = await processEncryptedResponse(res.data);
        showDecryptedLogs &&
          console.log("apiCall decrypted response:", decryptedData, typeof decryptedData);

        if (decryptedData?.status !== undefined) {
          status = decryptedData.status;
        } else if (res.data?.status !== undefined) {
          status = res.data?.status;
        } else {
          status = true;
        }

        if (decryptedData) {
          message = decryptedData?.message || decryptedData;
        } else if (typeof res?.data?.message === "string" || typeof res?.data?.data === "string") {
          message = res?.data?.message || res?.data?.data;
        }

        if (showToast) {
          if (status) {
            typeof message === "string" && toast.success(message);
          } else {
            typeof message === "string" && toast.error(message);
          }
        }
        return { response: res, decrypted: decryptedData, status, responseData: res.data };
      } catch (error) {
        // console.error("API Call error:", error);
        const decryptedData = await processEncryptedResponse(error.response?.data);
        showDecryptedLogs && console.log("apiCall error response:", decryptedData);
        if (showToast) {
          if (decryptedData) toast.error(decryptedData?.message || decryptedData);
          if (
            typeof error.response?.data?.message === "string" ||
            typeof error.response?.data?.data === "string"
          )
            toast.error(error.response?.data?.message || error.response?.data?.data);
        }

        if (error.response?.status === 401) {
          expireToken();
        }

        return { error: error.response, decrypted: decryptedData, status: false };
      } finally {
        setLoading(false);
        loader && hideLoader();
      }
    },
    [token, expireToken, toast],
  );

  return { callApi, loading };
};
export const fetchZones = async (token, expireToken) => {
  const encryptedRes = await getApi("superadmin/master/zones", token, expireToken);
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchRoles = async (token, expireToken) => {
  const encryptedRes = await getApi("superadmin/master/roles", token, expireToken);
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

// export const fetchQlUsersByZone = async (zone, token, expireToken) => {
//   const encryptedRes = await getApi(`superadmin/master/ql?zone=${zone}`, token, expireToken);
//   return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
// };
export const fetchQlUsersByZone = async (zone, roleId, token, expireToken) => {
  // decide type based on role
  const type = roleId === 10 ? "tl" : "ql";
  // 10 = Trainee (change if different)
  const encryptedRes = await getApi(`superadmin/master/${type}?zone=${zone}`, token, expireToken);
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};

export const fetchQlQuestions = async (auditTypeId, token, expireToken) => {
  const encryptedRes = await getApi(
    `superadmin/master/ql-questions/${auditTypeId}`,
    token,
    expireToken,
  );
  return encryptedRes ? await processEncryptedResponse(encryptedRes) : false;
};
